import React from "react";

import { Globe, Pencil, Trash2 } from "lucide-react";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { isGeoblockActive } from "@/src/utils/geoblock";

import { getSelectedCountries } from "../../../../utils/data/Countries";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  dateColumn,
  statusColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableCountCell from "../../../dataTable/DataTableCountCell";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { PROJECTS_ROUTE, smartlistEditPath } from "../../../routes/routes";
import { AvatarStack } from "../../AvatarStack";
import { restrictionsColumn } from "../../restrictionsColumn";
import { getSmartlistTypeIcon } from "../smartlistTypeIcons";

import type { TFunction } from "i18next";

import type { SmartlistRow } from "./smartlistColumns.types";
import type { SmartlistIndexListItem } from "../../../../api/SmartlistAPI.types";
import type { CountriesState } from "../../../../reducers/fetch.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";
import type { SmartlistSegment } from "../../../routes/routes.types";

interface SmartlistColumnsOptions {
  t: TFunction;
  projectSlug: string;
  segment: SmartlistSegment;
  showsElementsType: boolean;
  deleteTitle: string;
  getDeleteBody: (label: string) => string;
  onEdit: (smartlist: SmartlistRow) => void;
  onDelete: (smartlist: SmartlistRow) => void;
}

export function getSmartlistHref(
  projectSlug: string,
  segment: SmartlistSegment,
  uuid: string,
): string {
  return (
    smartlistEditPath(projectSlug, segment, uuid) ??
    `${PROJECTS_ROUTE}/${projectSlug}/${segment}`
  );
}

export function getGeoblockLines(
  smartlist: SmartlistIndexListItem,
  t: TFunction,
  countries: CountriesState,
): string[] {
  if (!isGeoblockActive(smartlist.geoblock_mode)) return [];

  const groupLabelList = (smartlist.country_groups ?? []).map(
    (countryGroup) => countryGroup.label,
  );
  const countryLabelList = getSelectedCountries(
    countries,
    smartlist.countries,
  ).map((country) => country.label);
  const labelList = [...groupLabelList, ...countryLabelList].filter(Boolean);

  return [
    `${t(`common:${smartlist.geoblock_mode}_countries`)} ${labelList.join(", ")}`,
  ];
}

export function smartlistColumns({
  t,
  projectSlug,
  segment,
  showsElementsType,
  deleteTitle,
  getDeleteBody,
  onEdit,
  onDelete,
}: SmartlistColumnsOptions): DataTableColumnDef<SmartlistRow>[] {
  return [
    statusColumn<SmartlistRow>("status", t("status"), [
      { value: "active", label: t("common:active"), tone: "success" },
      { value: "pause", label: t("common:pause"), tone: "warning" },
      { value: "stop", label: t("common:stop"), tone: "error" },
    ]),
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("title")} />
      ),
      meta: { searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getSmartlistHref(projectSlug, segment, row.original.uuid)}
        >
          {row.original.label}
        </DataTableLinkCell>
      ),
    },
    restrictionsColumn<SmartlistRow>({
      t,
      showsTemporalTooltip: true,
      getTooltipLineList: (smartlist) => smartlist.geoblock_lines,
    }),
    {
      id: "languages",
      accessorFn: (smartlist) => smartlist.trad_count,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("languages")} />
      ),
      meta: {
        label: t("languages"),
        width: "10%",
        minWidth: 120,
        align: "center",
      },
      cell: ({ row }) => {
        const languageList = row.original.languages_available.map((language) =>
          t(`languages:langs.${language}`),
        );
        if (!row.original.trad_count) return null;

        return (
          <span
            className="inline-flex align-middle"
            data-tooltip-id={APP_TOOLTIP_ID}
            data-tooltip-content={
              languageList.length
                ? `${t("languages")}: ${languageList.join(", ")}`
                : undefined
            }
          >
            <DataTableCountCell
              count={row.original.trad_count}
              icon={<Globe />}
            />
          </span>
        );
      },
    },
    {
      id: "elements",
      accessorFn: (smartlist) => smartlist.smartlist_items_count,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("elements")} />
      ),
      meta: {
        label: t("elements"),
        width: "10%",
        minWidth: 110,
        align: "center",
      },
      cell: ({ row }) => {
        if (!row.original.smartlist_items_count) return null;

        const TypeIcon = getSmartlistTypeIcon(row.original.type);
        const count = (
          <DataTableCountCell
            count={row.original.smartlist_items_count}
            icon={<TypeIcon />}
          />
        );
        if (!showsElementsType) return count;

        const typeLabel = t(`elementsType.${row.original.type}`);
        return (
          <span
            className="inline-flex align-middle"
            data-tooltip-content={typeLabel}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            {count}
            <span className="sr-only">{typeLabel}</span>
          </span>
        );
      },
    },
    {
      // `castings` est le nom du champ côté API ; l'en-tête reste « Éditeurs ».
      id: "castings",
      accessorFn: (smartlist) => smartlist.castings,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("curators")} />
      ),
      meta: {
        label: t("curators"),
        width: "10%",
        minWidth: 85,
        align: "center",
      },
      cell: ({ row }) => (
        <AvatarStack
          className="inline-flex"
          items={(row.original.castings ?? []).map((casting) => ({
            id: casting.casting_uuid,
            name: casting.name,
            avatarUrl: casting.picture,
          }))}
        />
      ),
    },
    dateColumn<SmartlistRow>("updated_at", t("update_date")),
    actionsColumn<SmartlistRow>(
      (smartlist) => [
        {
          label: t("edit"),
          icon: <Pencil />,
          onClick: () => onEdit(smartlist),
        },
        {
          label: t("delete"),
          icon: <Trash2 />,
          variant: "destructive",
          onClick: () => onDelete(smartlist),
          confirm: {
            title: deleteTitle,
            description: getDeleteBody(smartlist.label),
            confirmLabel: t("deleteConfirm"),
          },
        },
      ],
      (smartlist) => smartlist.label,
    ),
  ];
}
